<template>
  <div class="dropdown user-dropdown">
    
    <button 
      class="btn btn-outline-secondary btn-sm dropdown-toggle d-flex align-items-center" 
      type="button" 
      id="userDropdownMenu"
      @click="toggleDropdown"
    >
      <i class="fas fa-user-circle me-2"></i>
      <span class="d-none d-md-inline">{{ userName || 'User' }}</span>
    </button>
    <ul 
      class="dropdown-menu dropdown-menu-end" 
      :class="{ 'show': isOpen }"
      @click.stop
    >
      <li><h6 class="dropdown-header">{{ userName }}</h6></li>
      <li v-if="userEmail" class="px-3 text-muted small">{{ userEmail }}</li>
      <li><hr class="dropdown-divider"></li>
      
      <!-- User Profile/Settings -->
      <li>
        <router-link class="dropdown-item" :to="`/tech/edit/${userId}`" @click="closeDropdown">
          <i class="fas fa-user-edit me-2"></i>
          Edit Profile
        </router-link>
      </li>
      
      <li><hr class="dropdown-divider"></li>
      <li><h6 class="dropdown-header text-muted small">My Items</h6></li>
      
      <!-- My Requests -->
      <li>
        <router-link 
          class="dropdown-item" 
          :to="`/job/list?technician=${userId}`" 
          @click="closeDropdown"
        >
          <i class="fas fa-list me-2"></i>
          My Requests
        </router-link>
      </li>
      
      <!-- My Tasks -->
      <li>
        <router-link 
          class="dropdown-item" 
          :to="`/time/list?technician=${userId}`" 
          @click="closeDropdown"
        >
          <i class="fas fa-tasks me-2"></i>
          My Tasks
        </router-link>
      </li>
      
      <!-- My Parts -->
      <li>
        <router-link 
          class="dropdown-item" 
          :to="`/part/list?technician=${userId}`" 
          @click="closeDropdown"
        >
          <i class="fas fa-puzzle-piece me-2"></i>
          My Parts
        </router-link>
      </li>
      
      <!-- My Expenses -->
      <li>
        <router-link 
          class="dropdown-item" 
          :to="`/expense/list?technician=${userId}`" 
          @click="closeDropdown"
        >
          <i class="fas fa-hand-holding-usd me-2"></i>
          My Expenses
        </router-link>
      </li>
      
      <!-- My Mileage -->
      <li>
        <router-link 
          class="dropdown-item" 
          :to="`/mileage/list?technician=${userId}`" 
          @click="closeDropdown"
        >
          <i class="fas fa-car me-2"></i>
          My Mileage
        </router-link>
      </li>
      
      <!-- My Time Tracker -->
      <li>
        <router-link 
          class="dropdown-item" 
          to="/clock/list" 
          @click="closeDropdown"
        >
          <i class="fas fa-clock me-2"></i>
          My Time Tracker
        </router-link>
      </li>
      
      <li><hr class="dropdown-divider"></li>
      
      <!-- Logout -->
      <li>
        <router-link class="dropdown-item text-danger" to="/logout" @click="closeDropdown">
          <i class="fas fa-right-from-bracket me-2"></i>
          Logout
        </router-link>
      </li>
    </ul>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useAppStore } from '@/stores/AppStore';

const appStore = useAppStore();
const isOpen = ref(false);

// Get user info from store
const userName = computed(() => appStore.account.user?.User?.TechnicianName || 'User');
const userEmail = computed(() => appStore.account.user?.User?.Email || '');
const userId = computed(() => appStore.account.user?.User?.Id || '');

const toggleDropdown = () => {
  isOpen.value = !isOpen.value;
};

const closeDropdown = () => {
  isOpen.value = false;
};

// Close dropdown when clicking outside
const handleClickOutside = (event: MouseEvent) => {
  const dropdown = document.querySelector('.user-dropdown');
  if (dropdown && !dropdown.contains(event.target as Node)) {
    closeDropdown();
  }
};

onMounted(() => {
  document.addEventListener('click', handleClickOutside);
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
});
</script>

<style scoped>
.user-dropdown {
  position: relative;
}

.user-dropdown .dropdown-menu {
  min-width: 250px;
  margin-top: 0.5rem;
  right: 0;
  left: auto;
}

.user-dropdown .dropdown-item {
  padding: 0.5rem 1rem;
  transition: all 0.2s;
}

.user-dropdown .dropdown-item:hover {
  background-color: #f8f9fa;
  padding-left: 1.5rem;
}

.user-dropdown .dropdown-item i {
  width: 20px;
  text-align: center;
}

.user-dropdown .dropdown-header {
  font-weight: 600;
  color: #495057;
}
</style>
